import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingRecyclabilityAssessmentStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { isUnsupportedRecyclabilityAssessmentEntry } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import {
  formatRunTimestamp,
  PackagingRecyclabilityAssessmentRunResults,
} from './PackagingRecyclabilityAssessmentRunResults'
import {
  recyclabilityAssessmentGradeBadgeVariant,
  resolveRecyclabilityGradeForEntry,
} from './resolveRecyclabilityGradeFromAssessment'

type PackagingRecyclabilityAssessmentHistoryTableProps = {
  history: PackagingRecyclabilityAssessmentStoredEntry[]
  locale: string
}

function RecyclabilityHistorySummary({
  entry,
}: {
  entry: PackagingRecyclabilityAssessmentStoredEntry
}) {
  const { t } = useTranslation('packagingSuppliers')

  if (isUnsupportedRecyclabilityAssessmentEntry(entry)) {
    return (
      <Badge variant='outline'>
        {t('itemDetail.recyclability.unsupported.badge')}
      </Badge>
    )
  }

  const gradeResult = resolveRecyclabilityGradeForEntry(entry)
  if (gradeResult == null) {
    return (
      <span className='text-sm text-muted-foreground'>
        {t('itemDetail.recyclability.history.summaryUnavailable')}
      </span>
    )
  }

  let gradeLabel = t('itemDetail.recyclability.notRecyclable')
  if (gradeResult.grade === 'A' || gradeResult.grade === 'B' || gradeResult.grade === 'C') {
    gradeLabel = t('itemDetail.recyclability.gradeLabel', {
      grade: gradeResult.grade,
    })
  }

  return (
    <Badge variant={recyclabilityAssessmentGradeBadgeVariant(gradeResult.grade)}>
      {gradeLabel}
    </Badge>
  )
}

export function PackagingRecyclabilityAssessmentHistoryTable({
  history,
  locale,
}: PackagingRecyclabilityAssessmentHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    null
  )

  const selectedEntry = history.find(
    (entry) => entry.timestamp === selectedTimestamp
  )
  const selectedTitle =
    selectedEntry != null
      ? formatRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('itemDetail.recyclability.history.columnRun')}</TableHead>
            <TableHead>
              {t('itemDetail.recyclability.history.columnVersion')}
            </TableHead>
            <TableHead>
              {t('itemDetail.recyclability.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.recyclability.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const runLabel = formatRunTimestamp(entry.timestamp, locale)
            return (
              <TableRow key={entry.timestamp}>
                <TableCell className='font-medium'>{runLabel}</TableCell>
                <TableCell>
                  {t('itemDetail.recyclability.version', {
                    version: entry.assessmentVersion,
                  })}
                </TableCell>
                <TableCell>
                  <RecyclabilityHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={() => {
                      setSelectedTimestamp(entry.timestamp)
                    }}
                  >
                    {t('itemDetail.recyclability.history.viewResults')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTimestamp(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null ? (
          <PackagingRecyclabilityAssessmentRunResults result={selectedEntry} />
        ) : null}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
